import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

interface StepButtonsProps {
  onBack: () => void;
  /** Leave out in a form: the confirm button then submits it. */
  onConfirm?: () => void;
  /** What the confirm button says: "Inplannen". */
  confirm: string;
  /** The step runs: the confirm button spins and does nothing more (its handler checks too). */
  pending: boolean;
  disabled?: boolean;
  destructive?: boolean;
  /**
   * Annuleren takes the focus when the buttons show: under a question that
   * replaced the button the keyboard was on, such as Verwijderen.
   */
  autoFocusBack?: boolean;
}

/**
 * Annuleren and a step's own button, right-aligned: the buttons under a
 * question an action asks first, in an item's details or in a small dialog.
 * While the step runs, the confirm button keeps its name and the focus: it is
 * never `disabled` for it, which would drop the focus, only `aria-disabled`.
 */
export function StepButtons({
  onBack,
  onConfirm,
  confirm,
  pending,
  disabled = false,
  destructive = false,
  autoFocusBack = false,
}: StepButtonsProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button type="button" variant="outline" size="sm" onClick={onBack} autoFocus={autoFocusBack}>
        {t("common.cancel")}
      </Button>
      <Button
        type={onConfirm ? "button" : "submit"}
        size="sm"
        variant={destructive ? "destructive" : "default"}
        onClick={pending ? undefined : onConfirm}
        disabled={disabled && !pending}
        aria-disabled={pending || undefined}
        className="aria-disabled:cursor-wait"
        data-step="confirm"
      >
        {pending && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
        {confirm}
      </Button>
    </div>
  );
}
